import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Twitter, Facebook, Linkedin, Instagram, Github, Sparkles } from "lucide-react";
import "./LanderFooter.css";

type LanderFooterProps = {
  showCta?: boolean;
};

const LanderFooter: React.FC<LanderFooterProps> = ({ showCta = true }) => {
  const [showTabby, setShowTabby] = useState(false);

  useEffect(() => {
    try {
      const region = new URLSearchParams(window.location.search).get('region');
      if (region) {
        const r = region.toLowerCase();
        if (['ae', 'uae', 'sa', 'saudi', 'saudi arabia'].includes(r)) {
          setShowTabby(true);
        }
      }
    } catch (e) {
      // ignore
    }
  }, []);

  return (
    <footer className="cb-footer">
      {showCta && (
        <div className="cb-footer-top">
          <h2>
            <span>Let your website speak for itself.</span>
            <img
              src="https://img.icons8.com/?size=100&id=G6ICeTLs79X1&format=png&color=FF8A4C"
              alt=""
              className="cb-footer-title-icon"
            />
          </h2>
          <p>
            SmushChat turns business knowledge into useful conversations—answering questions, spotting intent, and bringing in your team when human judgment matters.
          </p>
          <div className="cb-footer-ctas">
            <Link to="/pricing" className="cb-btn-primary">
              View plans
            </Link>
            <Link to="/#demo" className="cb-btn-secondary">
              Request a custom demo
            </Link>
          </div>
        </div>
      )}

      <div className="cb-footer-main">
        <div className="cb-footer-brand">
          <div className="cb-footer-brand-header">
            <img src="/favicon.ico" alt="SmushChat Logo" className="cb-footer-logo" />
            <span className="cb-footer-brand-name">SmushLabs</span>
          </div>
          <div className="cb-footer-socials-row">
            <Link to="/legal/contact-us" className="cb-social-btn-text">Contact</Link>
            <a href="https://www.linkedin.com/company/smushlabs" className="cb-social-btn-icon" aria-label="LinkedIn">
              <Linkedin size={18} />
            </a>
            <a href="https://www.facebook.com/smushlabs" className="cb-social-btn-icon" aria-label="Facebook">
              <Facebook size={18} />
            </a>
            <a href="https://twitter.com/smushlabs_ai" className="cb-social-btn-icon" aria-label="Twitter">
              <Twitter size={18} />
            </a>
            <a href="https://www.instagram.com/smushlabs/" className="cb-social-btn-icon" aria-label="Instagram">
              <Instagram size={18} />
            </a>
            <a href="https://github.com/smushlabs" className="cb-social-btn-icon" aria-label="GitHub">
              <Github size={18} />
            </a>
          </div>

          <div className="cb-footer-brand-badges">
            {/* Peer Push Badge */}
            <a 
              href="https://peerpush.net/p/smushchat-ai-chatbot-agent-builder/"
              target="_blank"
              rel="noopener noreferrer"
              className="brand-badge-peerpush"
            >
              <img 
                src="https://peerpush.net/p/smushchat-ai-chatbot-agent-builder/badge.png"
                alt="SmushChat AI Chatbot Agent Builder badge"
              />
            </a>
          </div>
          <div className="cb-footer-meta">
            <Link to="/llm-info" className="cb-footer-ai-link">
              <Sparkles aria-hidden="true" size={14} />
              <span>Hey AI, learn about us</span>
            </Link>
            <span>© {new Date().getFullYear()} SmushLabs</span>
          </div>
          <div className="cb-footer-payment-icons">
            <img src="https://img.icons8.com/color/48/000000/visa.png" alt="Visa" />
            <img src="https://img.icons8.com/color/48/000000/mastercard.png" alt="Mastercard" />
            <img src="https://img.icons8.com/color/48/000000/amex.png" alt="American Express" />
            <img src="https://img.icons8.com/color/48/000000/paypal.png" alt="PayPal" />
            <img src="https://img.icons8.com/color/48/000000/apple-pay.png" alt="Apple Pay" />
            <img src="https://img.icons8.com/color/48/000000/google-pay.png" alt="Google Pay" />
            {showTabby && (
              <img src="https://mintcdn.com/tabby-5f40add6/1f-EIKwKexPNgWGm/logo/tabby-badge.svg" alt="Tabby" style={{ height: '24px', objectFit: 'contain' }} />
            )}
          </div>
        </div>
        
        <div className="cb-footer-links-grid">
          <div className="cb-footer-col">
            <h6>PRODUCT</h6>
            <ul>
              <li><a href="/">Home</a></li>
              <li><a href="/features">Features</a></li>
              <li><a href="/pricing">Pricing</a></li>
              <li><a href="/blog">Blog</a></li>
              <li><a href="/launch-deal">Launch Deal</a></li>
              <li><a href="/research">How it works</a></li>
              <li><a href="/#demo">Demo</a></li>
            </ul>
          </div>
          
          <div className="cb-footer-col">
            <h6>PLATFORM</h6>
            <ul>
              <li><a href="/education">Education</a></li>
              {/* TODO: Restore additional use-case links only after their pages are ready. */}
              <li><a href="/login">Login</a></li>
              <li><a href="/get-started">Get Started</a></li>
              <li><a href="/dashboard">Dashboard</a></li>
            </ul>
          </div>

          <div className="cb-footer-col">
            <h6>RESOURCES</h6>
            <ul>
              <li><a href="/guides">Guides</a></li>
              <li><a href="/research">Research</a></li>
              <li><a href="/blog">Blog</a></li>
              <li><a href="/affiliates">Affiliate Program</a></li>
              <li><a href="/legal/contact-us">Contact us</a></li>
              {/* TODO: Restore Changelog, API Docs, and Roadmap links only after their public pages are ready. */}
            </ul>
          </div>

          <div className="cb-footer-col">
            <h6>POLICIES</h6>
            <ul>
              <li><a href="/legal/free-demo-policy">Free demo policy</a></li>
              <li><a href="/legal/terms-conditions">Terms of service</a></li>
              <li><a href="/legal/privacy-policy">Privacy policy</a></li>
              <li><a href="/legal/cancellation-refund">Refund policy</a></li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="cb-footer-brand-text">
        SMUSHCHAT
      </div>

    </footer>
  );
};

export default LanderFooter;
